// Mock data and API service for Endemik Solutions Shawl Testing Laboratory
const RAW_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// This automatically handles the base domain and guarantees a single clean "/api" suffix
const API_URL = `${RAW_URL.replace(/\/+$/, '')}`.endsWith('/api') 
  ? RAW_URL.replace(/\/+$/, '') 
  : `${RAW_URL.replace(/\/+$/, '')}/api`;

// Fixed: Removed duplicate manual /api tracking prefix
fetch(`${API_URL}/services`)
  .then(res => res.json())
  .catch(err => console.error('Failed to fetch services', err));


export const services = [
  {
    id: 1,
    name: "Pashmina Authentication",
    description: "Comprehensive testing to verify the authenticity of Pashmina shawls using advanced laboratory techniques. Our certified experts use state-of-the-art equipment to ensure complete accuracy.",
    price: 1500,
    duration: "3-5 business days",
    icon: "shield-check",
    features: [
      "Laboratory verification",
      "Authenticity certification",
      "Detailed report",
      "Quality assessment"
    ]
  },
  {
    id: 2,
    name: "Adulteration Testing",
    description: "Detailed analysis to detect any adulterants or synthetic fibers mixed with natural shawl materials. We identify exact composition and purity levels.",
    price: 1500,
    duration: "5-7 business days",
    icon: "microscope",
    features: [
      "Fiber composition analysis",
      "Adulteration detection",
      "Purity certification",
      "Comparative report"
    ]
  }
];

export const bookings = [
  {
    id: "BK001",
    bookingId: "BK000001",
    customerName: "Rajesh Kumar",
    email: "rajesh@example.com",
    phoneNumber: "+91 98765 43210",
    serviceType: "Pashmina Authentication",
    preferredDate: "2025-08-20",
    preferredTime: "10:00 AM",
    sampleDetails: "Red Pashmina shawl from Kashmir",
    status: "pending",
    estimatedCost: 1500,
    notes: "",
    createdAt: "2025-06-05T10:30:00Z",
    emailSent: true,
    confirmationSent: true,
  },
  {
    id: "BK002",
    bookingId: "BK000002",
    customerName: "Priya Singh",
    email: "priya@example.com",
    phoneNumber: "+91 87654 32109",
    serviceType: "Adulteration Testing",
    preferredDate: "2025-08-22",
    preferredTime: "02:00 PM",
    sampleDetails: "White Pashmina shawl",
    status: "approved",
    estimatedCost: 1500,
    notes: "Sample received and in queue",
    createdAt: "2025-06-04T14:15:00Z",
    emailSent: true,
    confirmationSent: true,
  },
  {
    id: "BK003",
    bookingId: "BK000003",
    customerName: "Amit Patel",
    email: "amit@example.com",
    phoneNumber: "+91 76543 21098",
    serviceType: "Pashmina Authentication",
    preferredDate: "2025-08-25",
    preferredTime: "11:00 AM",
    sampleDetails: "Blue Pashmina shawl with embroidery",
    status: "completed",
    estimatedCost: 1500,
    notes: "Testing completed. Report generated.",
    createdAt: "2025-06-03T09:45:00Z",
    emailSent: true,
    confirmationSent: true,
  },
];

export const pricingData = [
  {
    id: 1,
    name: "Pashmina Authentication",
    description: "Basic authenticity verification",
    basePrice: 1500,
    duration: "3-5 business days",
    features: [
      "Visual inspection",
      "Fiber testing",
      "Authenticity report",
      "Email certificate"
    ]
  },
  {
    id: 2,
    name: "Adulteration Testing",
    description: "Detailed composition analysis",
    basePrice: 1500,
    duration: "5-7 business days",
    features: [
      "Complete fiber analysis",
      "Adulteration detection",
      "Purity percentage",
      "Comparative chart",
      "Email report"
    ]
  }
];

export const companyInfo = {
  name: "Endemik Solutions Private Limited",
  tagline: "Authentic Testing, Trusted Results",
  email: "support@endemiksolutions.com",
  phone: "+91 9767-860-434",
  address: "Naroo, Budgam, J&K, 191111, India",
  workingHours: "Monday - Friday: 9:00 AM - 6:00 PM IST",
  about: "Endemik Solutions is a certified testing laboratory specializing in the authentication and quality analysis of premium Pashmina shawls. With state-of-the-art equipment and experienced professionals, we provide accurate and reliable testing services to ensure the authenticity of your valuable textiles."
};

// ============= API SERVICE FUNCTIONS =============

// Authentication
export const authService = {
  login: async (email, password) => {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await response.json();
    if (data.success) {
      localStorage.setItem('authToken', data.token);
    }
    return data;
  },

  register: async (name, email, password) => {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    });
    const data = await response.json();
    if (data.success) {
      localStorage.setItem('authToken', data.token);
    }
    return data;
  },

  logout: () => {
    localStorage.removeItem('authToken');
  },

  getCurrentUser: async () => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.json();
  },
};

// Bookings
export const bookingService = {
  createBooking: async (bookingData) => {
    const response = await fetch(`${API_URL}/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData),
    });
    return response.json();
  },

  getAllBookings: async (filters = {}) => {
    const token = localStorage.getItem('authToken');
    const params = new URLSearchParams(filters);
    const response = await fetch(`${API_URL}/bookings?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.json();
  },

  getBooking: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/bookings/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.json();
  },

  searchBooking: async (bookingId) => {
    const response = await fetch(`${API_URL}/bookings/search/${bookingId}`);
    return response.json();
  },

  updateStatus: async (id, status, notes = '') => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/bookings/${id}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ status, notes }),
    });
    return response.json();
  },

  getStats: async () => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/bookings/stats/overview`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.json();
  },

  deleteBooking: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/bookings/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.json();
  },
};

// Services
export const serviceService = {
  getAllServices: async () => {
    const response = await fetch(`${API_URL}/services`);
    return response.json();
  },

  updatePricing: async (id, basePrice) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/services/${id}/pricing`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ basePrice }),
    });
    return response.json();
  },
};

// Admin
export const adminService = {
  getAllUsers: async () => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/admin/users`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.json();
  },

  createUser: async (userData) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/admin/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    });
    return response.json();
  },

  updateUser: async (id, userData) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/admin/users/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    });
    return response.json();
  },

  deleteUser: async (id) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_URL}/admin/users/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return response.json();
  },
};

// ============= MOCK DATA FUNCTIONS (Fallback) =============

export const updateBookingStatus = async (bookingId, newStatus) => {
  // This will be replaced by actual API call
  return { success: true };
};

export const updatePricing = async (serviceId, newPrice) => {
  // This will be replaced by actual API call
  return { success: true };
};